import { Card, CardContent, CardHeader, CardTitle } from '@cibli/shared';
import '@micdrop/web/silero';
import { useMicdropState } from '@micdrop/react';
import { Micdrop, MultipleVAD, SileroVAD, VolumeVAD } from '@micdrop/web';
import { Mic, MicOff, Volume2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { VadValues } from './vad-config';

import { MediaDevices } from './MediaDevices';
import MicVolume from './MicVolume';
import SpeakerTestButton from './SpeakerTestButton';
import { loadVadValues, saveVadValues } from './vad-config';
import { VadSettingsPanel } from './vad-settings-panel';

/** Apply the panel values to the live VAD instances held by Micdrop. */
function applyVadValues(values: VadValues): void {
  const vad = Micdrop.vad;
  if (!vad) return;
  const vads = vad instanceof MultipleVAD ? vad.vads : [vad];
  for (const instance of vads) {
    if (instance instanceof VolumeVAD) {
      instance.setOptions({
        history: values['volume.history'],
        threshold: values['volume.threshold'],
      });
    } else if (instance instanceof SileroVAD) {
      instance.setOptions({
        positiveSpeechThreshold: values['silero.positiveSpeechThreshold'],
        negativeSpeechThreshold: values['silero.negativeSpeechThreshold'],
        minSpeechFrames: values['silero.minSpeechFrames'],
        redemptionFrames: values['silero.redemptionFrames'],
      });
    }
  }
}

export default function DevicesSettings() {
  const { isMicStarted, micDeviceId, micDevices, speakerDeviceId, speakerDevices, isMuted } =
    useMicdropState();
  const { t } = useTranslation();
  // Persisted across session reset and app reload (localStorage).
  const [vadValues, setVadValues] = useState<VadValues>(loadVadValues);

  // Start mic if it was already started
  useEffect(() => {
    if (isMicStarted) return;
    console.warn(
      '[Micdrop][Settings] Démarrage micro local - VAD: [volume, silero] (Silero VAD v5 embarqué)',
    );
    Micdrop.startMic({
      vad: ['volume', 'silero'],
    }).catch(err => {
      console.error('[Micdrop][Settings][Error] Impossible de démarrer le micro:', err);
    });
    return () => {
      if (!Micdrop.isStarted) {
        Micdrop.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply persisted/edited values to the live VAD (fresh start or mic
  // started elsewhere in the app).
  useEffect(() => {
    if (!isMicStarted) return;
    applyVadValues(vadValues);
  }, [isMicStarted, vadValues]);

  const handleVadChange = (next: VadValues): void => {
    setVadValues(next);
    saveVadValues(next);
  };

  if (!isMicStarted) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Settings.audioDevices')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <button onClick={isMuted ? Micdrop.unmute : Micdrop.mute}>
            {isMuted ? (
              <MicOff className="text-gray-600 text-xl" />
            ) : (
              <Mic className="text-gray-600 text-xl" />
            )}
          </button>
          <select
            value={micDeviceId}
            className="flex-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 py-2"
            onChange={event => Micdrop.changeMicDevice(event.target.value)}
          >
            {micDevices.map(({ id, label }) => (
              <option key={id} value={id}>
                {label || 'Microphone'}
              </option>
            ))}
          </select>
          <div className="w-[150px] max-w-[30%]">
            <MicVolume />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Volume2 className="text-gray-600 text-xl" />
          <select
            value={speakerDeviceId}
            className="flex-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 py-2"
            onChange={event => Micdrop.changeSpeakerDevice(event.target.value)}
          >
            {speakerDevices.map(({ id, label }) => (
              <option key={id} value={id}>
                {label || 'Speaker'}
              </option>
            ))}
          </select>
          <div className="w-[150px] max-w-[30%]">
            <SpeakerTestButton />
          </div>
        </div>

        <VadSettingsPanel values={vadValues} onChange={handleVadChange} />
        <MediaDevices />
      </CardContent>
    </Card>
  );
}
